import { Transform, Type } from 'class-transformer';
import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsBoolean,
	IsEnum,
	IsIn,
	IsInt,
	IsISO8601,
	IsNumber,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Max,
	MaxLength,
	Min,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
	Validate,
} from 'class-validator';
import { CoursesAiModel, CoursesChallengeStatus } from '../../../enums/courses.enum';

/** Stays well below the temporary parking band used while reordering. */
const MAX_QUESTION_ORDER = 100_000;

const trimString = ({ value }: { value: unknown }) =>
	typeof value === 'string' ? value.trim() : value;

/** Anything the WHATWG `URL` parser accepts; the scheme is checked by the consumer. */
@ValidatorConstraint({ name: 'coursesParsableUrl', async: false })
export class CoursesParsableUrlConstraint implements ValidatorConstraintInterface {
	protected isAllowedProtocol(_protocol: string): boolean {
		return true;
	}

	validate(value: unknown): boolean {
		if (typeof value !== 'string') {
			return false;
		}
		try {
			return this.isAllowedProtocol(new URL(value).protocol);
		} catch {
			return false;
		}
	}

	defaultMessage(args: ValidationArguments): string {
		return `${args.property} must be a valid URL`;
	}
}

/** Only http(s): other schemes (e.g. `javascript:`) are unsafe to render as links. */
@ValidatorConstraint({ name: 'coursesHttpUrl', async: false })
export class CoursesHttpUrlConstraint extends CoursesParsableUrlConstraint {
	protected override isAllowedProtocol(protocol: string): boolean {
		return protocol === 'http:' || protocol === 'https:';
	}

	override defaultMessage(args: ValidationArguments): string {
		return `${args.property} must be a valid http(s) URL`;
	}
}

export class CoursesChallengeIdParamDto {
	@IsUUID()
	challengeId: string;
}

export class CoursesChallengeEntityIdParamDto {
	@IsUUID()
	id: string;
}

export class CoursesChallengeParticipantParamDto {
	@IsUUID()
	challengeId: string;

	@IsUUID()
	studentId: string;
}

export class CoursesSubmitChallengeResponseDto {
	@IsUUID()
	challengeId: string;

	@IsUUID()
	questionId: string;

	@IsNumber()
	@Min(0)
	@Max(1)
	probability: number;

	@IsOptional()
	@IsString()
	@MaxLength(1000)
	reasoning?: string;
}

export class CoursesAdminUpsertChallengeDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@Transform(trimString)
	@IsString()
	@Length(1, 200)
	title: string;

	@IsOptional()
	@ValidateIf((o) => o.description !== null)
	@Transform(trimString)
	@IsString()
	@MaxLength(4000)
	description?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.event_name !== null)
	@Transform(trimString)
	@IsString()
	@MaxLength(200)
	event_name?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.event_date !== null)
	@IsISO8601()
	event_date?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.event_start_gmt !== null)
	@IsISO8601()
	event_start_gmt?: string | null;

	@IsISO8601()
	submission_deadline: string;

	@IsOptional()
	@ValidateIf((o) => o.video_url !== null && o.video_url !== '')
	@Validate(CoursesHttpUrlConstraint)
	video_url?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.external_link !== null && o.external_link !== '')
	@Validate(CoursesHttpUrlConstraint)
	external_link?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.banner_path !== null)
	@Transform(trimString)
	@IsString()
	@MaxLength(500)
	banner_path?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.master_name !== null)
	@Transform(trimString)
	@IsString()
	@MaxLength(120)
	master_name?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.message_to_participants !== null)
	@Transform(trimString)
	@IsString()
	@MaxLength(4000)
	message_to_participants?: string | null;

	@IsOptional()
	@ValidateIf((o) => o.leaderboard_weight !== null)
	@IsNumber()
	@Min(0)
	@Max(10)
	leaderboard_weight?: number | null;

	@IsEnum(CoursesChallengeStatus)
	status: CoursesChallengeStatus;

	@IsBoolean()
	published: boolean;

	@IsOptional()
	@IsUUID()
	course_id?: string;
}

export class CoursesAdminUpsertQuestionDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@IsUUID()
	challenge_id: string;

	@Transform(trimString)
	@IsString()
	@Length(1, 500)
	text: string;

	@IsInt()
	@Min(0)
	@Max(MAX_QUESTION_ORDER)
	order: number;
}

export class CoursesAdminQuestionOrderItemDto {
	@IsUUID()
	id: string;

	@IsInt()
	@Min(0)
	@Max(MAX_QUESTION_ORDER)
	order: number;
}

export class CoursesAdminReorderQuestionsDto {
	@IsUUID()
	challenge_id: string;

	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(200)
	@ValidateNested({ each: true })
	@Type(() => CoursesAdminQuestionOrderItemDto)
	order: CoursesAdminQuestionOrderItemDto[];
}

export class CoursesAdminSetMasterPredictionDto {
	@IsUUID()
	question_id: string;

	/** Required but nullable: `null` clears the master prediction. */
	@ValidateIf((o) => o.probability !== null)
	@IsNumber()
	@Min(0)
	@Max(1)
	probability: number | null;

	@IsOptional()
	@ValidateIf((o) => o.rationale !== null)
	@IsString()
	@MaxLength(2000)
	rationale?: string | null;
}

export class CoursesAdminSetOutcomeDto {
	@IsUUID()
	question_id: string;

	/** Required but nullable: `null` clears the outcome. */
	@ValidateIf((o) => o.happened !== null)
	@IsBoolean()
	happened: boolean | null;
}

export class CoursesAdminSetAiPredictionDto {
	@IsUUID()
	question_id: string;

	@IsEnum(CoursesAiModel)
	model: CoursesAiModel;

	/** Required but nullable: `null` clears this model's prediction. */
	@ValidateIf((o) => o.probability !== null)
	@IsNumber()
	@Min(0)
	@Max(1)
	probability: number | null;
}

export const COURSES_PARTICIPANT_SORT_FIELDS = [
	'responded',
	'submitted',
	'name',
	'brier',
	'rank',
] as const;
export type CoursesParticipantSortField = (typeof COURSES_PARTICIPANT_SORT_FIELDS)[number];

export const COURSES_PARTICIPANT_SORT_DIRS = ['asc', 'desc'] as const;
export type CoursesParticipantSortDir = (typeof COURSES_PARTICIPANT_SORT_DIRS)[number];

export const COURSES_PARTICIPANTS_MAX_LIMIT = 500;
export const COURSES_PARTICIPANTS_DEFAULT_LIMIT = 25;
export const COURSES_PARTICIPANTS_MAX_OFFSET = 1_000_000;

/*
 * Tolerant coercion: out-of-range or unknown
 * values never fail validation, they are coerced, clamped or replaced by safe defaults.
 * A non-finite `Number(value)` falls back to the default.
 */
export function coerceCoursesParticipantsOffset(value: unknown): number {
	const n = Number(value);

	return Number.isFinite(n)
		? Math.min(COURSES_PARTICIPANTS_MAX_OFFSET, Math.max(0, Math.trunc(n)))
		: 0;
}

export function coerceCoursesParticipantsLimit(value: unknown): number {
	const n = Number(value);

	return Number.isFinite(n)
		? Math.min(COURSES_PARTICIPANTS_MAX_LIMIT, Math.max(1, Math.trunc(n)))
		: COURSES_PARTICIPANTS_DEFAULT_LIMIT;
}

export function coerceCoursesParticipantsSortBy(value: unknown): CoursesParticipantSortField {
	return typeof value === 'string' &&
		(COURSES_PARTICIPANT_SORT_FIELDS as readonly string[]).includes(value)
		? (value as CoursesParticipantSortField)
		: 'responded';
}

export function coerceCoursesParticipantsSortDir(value: unknown): CoursesParticipantSortDir {
	return typeof value === 'string' &&
		(COURSES_PARTICIPANT_SORT_DIRS as readonly string[]).includes(value)
		? (value as CoursesParticipantSortDir)
		: 'desc';
}

/**
 * Query for `adminListParticipants` (challengeId is in the path). Absent keys are not passed to
 * `@Transform`, so the service re-applies the same coercers to fill defaults.
 */
export class CoursesAdminListParticipantsQueryDto {
	@IsOptional()
	@Transform(({ value }) => coerceCoursesParticipantsOffset(value))
	offset?: number;

	@IsOptional()
	@Transform(({ value }) => coerceCoursesParticipantsLimit(value))
	limit?: number;

	@IsOptional()
	@Transform(({ value }) => coerceCoursesParticipantsSortBy(value))
	sortBy?: CoursesParticipantSortField;

	@IsOptional()
	@Transform(({ value }) => coerceCoursesParticipantsSortDir(value))
	sortDir?: CoursesParticipantSortDir;
}

export const COURSES_CHALLENGE_EMAIL_TEMPLATE_KEYS = [
	'invitation',
	'reminder',
	'results',
	'leaderboard',
] as const;
export type CoursesChallengeEmailTemplateKey =
	(typeof COURSES_CHALLENGE_EMAIL_TEMPLATE_KEYS)[number];

export const COURSES_CHALLENGE_EMAIL_SEND_MODES = ['off', 'auto', 'manual'] as const;
export type CoursesChallengeEmailSendMode = (typeof COURSES_CHALLENGE_EMAIL_SEND_MODES)[number];

export class CoursesAdminUpsertChallengeEmailSettingDto {
	@IsUUID()
	challenge_id: string;

	@IsIn(COURSES_CHALLENGE_EMAIL_TEMPLATE_KEYS)
	template_key: CoursesChallengeEmailTemplateKey;

	@IsIn(COURSES_CHALLENGE_EMAIL_SEND_MODES)
	send_mode: CoursesChallengeEmailSendMode;
}
